import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ShieldCheck,
  AlertCircle,
  Loader2,
  Mail,
  Smartphone,
} from 'lucide-react';

/* ================================================================== */
/*  VerificationCode — enter the 6-digit code sent by email           */
/*                                                                     */
/*  Branding follows the Ratified login/auth-code screen:              */
/*  centered card, brand blue (#375AE7) accents, large code inputs,    */
/*  full-width brand action button.                                    */
/*                                                                     */
/*  USAGE                                                              */
/*  ------------------------------------------------------------------ */
/*  <VerificationCode                                                  */
/*    email="jane@company.com.au"                                      */
/*    onVerify={(code) => { ... }}          // 6-digit code accepted  */
/*    verifyCode={async (code) => { ... }} // OPTIONAL: your real API */
/*  />                                                                 */
/*                                                                     */
/*  • If verifyCode is provided it is called with the 6-digit string; */
/*    throw / reject to show the inline error state. If omitted,      */
/*    verification is simulated (900ms) so the screen works alone.    */
/*  • The component handles: auto-advance, backspace/arrow navigation,*/
/*    paste-to-fill, auto-submit when all 6 digits are entered,       */
/*    resend cooldown (30s) and the success hand-off to onVerify.     */
/*                                                                     */
/*  MERGING INTO YOUR PROJECT                                          */
/*  ------------------------------------------------------------------ */
/*  Self-contained — just copy this file and import it. Needs         */
/*  lucide-react and Tailwind with a `brand` color token (or swap      */
/*  `brand` for #375AE7). Point LOGO_URL at your own Ratified logo.   */
/* ================================================================== */

/* Your Ratified logo (demo serves it from /public). */
const LOGO_URL = '/logo-watermark.png';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 30; // seconds

const emptyCode = () => Array(CODE_LENGTH).fill('');

/*  Extra optional props (email flow keeps working without them):      */
/*    onResend    async fn that really sends a new code, throw to show   */
/*                the error banner                                     */
/*    channel     'email' (default) or 'mobile' for the SMS OTP step    */
/*    destination what to show in the pill, eg the masked phone number  */
const VerificationCode = ({
  email,
  onVerify,
  verifyCode,
  onResend,
  channel = 'email',
  destination,
}) => {
  // same screen is reused for the mobile OTP step, email is still the default
  const isMobileChannel = channel === 'mobile';
  const shownDestination = destination || email;
  const ChannelIcon = isMobileChannel ? Smartphone : Mail;
  const channelWord = isMobileChannel ? 'mobile' : 'email';
  const [code, setCode] = useState(emptyCode());
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [resent, setResent] = useState(false);
  const inputsRef = useRef([]);

  const focusAt = (i) =>
    inputsRef.current[Math.max(0, Math.min(CODE_LENGTH - 1, i))]?.focus();

  /* ----------------------------- resend ----------------------------- */
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    focusAt(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    if (cooldown > 0 || loading) return;
    setCooldown(RESEND_COOLDOWN);
    setError(null);
    // when the parent gives us onResend we actualy ask the API for a new code
    if (onResend) {
      try {
        await onResend();
      } catch (err) {
        setError(
          err?.message || "We couldn't send a new code. Please try again.",
        );
        return;
      }
    }
    setResent(true);
    setTimeout(() => setResent(false), 4000);
  };

  /* --------------------------- verification ------------------------- */
  const runVerify = useCallback(
    async (digits) => {
      if (loading) return;
      if (digits.join('').length < CODE_LENGTH) {
        setError(`Please enter all ${CODE_LENGTH} digits of the code.`);
        return;
      }
      setError(null);
      setLoading(true);
      try {
        if (verifyCode) {
          await verifyCode(digits.join(''));
        } else {
          /* No API provided — simulate a check so the screen works. */
          await new Promise((r) => setTimeout(r, 900));
        }
        onVerify?.(digits.join(''));
      } catch (err) {
        setError(
          err?.message ||
            `That code didn't match. Check your ${channelWord} and try again.`,
        );
        setCode(emptyCode());
        focusAt(0);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, verifyCode, onVerify],
  );

  /* Auto-submit once all six digits are in. */
  useEffect(() => {
    if (!loading && code.every((d) => d !== '')) runVerify(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  /* --------------------------- input wiring ------------------------- */
  const handleChange = (value, i) => {
    const d = value.replace(/\D/g, '');
    const next = [...code];
    if (!d) {
      next[i] = '';
      setCode(next);
      return;
    }
    next[i] = d[d.length - 1]; // keep the last typed/pasted digit
    setCode(next);
    if (i < CODE_LENGTH - 1) focusAt(i + 1);
  };

  const handleKeyDown = (e, i) => {
    if (e.key === 'Backspace' && !code[i] && i > 0) {
      e.preventDefault();
      const next = [...code];
      next[i - 1] = '';
      setCode(next);
      focusAt(i - 1);
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'ArrowRight' && i < CODE_LENGTH - 1) {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = (e.clipboardData || window.clipboardData)
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH)
      .split('');
    if (!digits.length) return;
    const next = emptyCode();
    digits.forEach((d, idx) => (next[idx] = d));
    setCode(next);
    focusAt(Math.min(digits.length, CODE_LENGTH - 1));
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-start justify-center px-4 py-8 sm:py-10 relative overflow-hidden">
      <div className="relative w-full max-w-md">
        <div className="rounded-3xl border border-slate-200/80 bg-white shadow-[0_24px_64px_-32px_rgba(15,23,42,0.4)] overflow-hidden animate-fade-in">
          {/* Brand header */}
          <div className="flex flex-col items-center px-8 pt-9 pb-2 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 ring-1 ring-brand/15">
              <img
                src="/Ratified-new.svg"
                alt="Ratified"
                className="h-8 w-8 object-contain"
              />
            </span>
            <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.22em] text-brand">
              Ratified
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">
              Verify your {channelWord}
            </h1>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-400">
              Enter the 6-digit code we sent to your {channelWord}
            </p>
            <span className="mt-2.5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-[12.5px] font-semibold text-slate-600">
              <ChannelIcon size={13} className="text-brand" />
              {shownDestination}
            </span>
          </div>

          {/* Form */}
          <div className="px-8 pb-8 pt-6">
            {/* Error banner */}
            {error && (
              <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50/70 px-3.5 py-3 text-[12.5px] font-medium leading-snug text-red-600 animate-fade-in">
                <AlertCircle size={15} className="mt-px shrink-0" />
                {error}
              </div>
            )}
            {resent && !error && (
              <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-brand/20 bg-brand/5 px-3.5 py-3 text-[12.5px] font-medium leading-snug text-brand animate-fade-in">
                <ChannelIcon size={15} className="mt-px shrink-0" />A new code
                is on its way to {shownDestination}.
              </div>
            )}

            {/* Code inputs */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                runVerify(code);
              }}
            >
              <div
                onPaste={handlePaste}
                className="flex w-full gap-2 sm:gap-2.5"
              >
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputsRef.current[index] = el)}
                    id={`code-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength="1"
                    value={digit}
                    disabled={loading}
                    aria-label={`Digit ${index + 1}`}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={(e) => e.target.select()}
                    className={`h-14 flex-1 min-w-0 appearance-none rounded-xl border-2 bg-white text-center text-lg font-bold leading-none text-slate-900 shadow-sm transition-all duration-200 focus:outline-none disabled:opacity-60 ${
                      error
                        ? 'border-red-300 bg-red-50/40 focus:border-red-400 focus:ring-4 focus:ring-red-100'
                        : digit
                          ? 'border-brand/60 bg-brand/[0.03] focus:ring-4 focus:ring-brand/10'
                          : 'border-slate-200 hover:border-slate-300 focus:border-brand focus:ring-4 focus:ring-brand/10'
                    }`}
                  />
                ))}
              </div>

              {/* Resend row */}
              <div className="mt-5 flex items-center justify-center gap-1.5 text-[12.5px] text-slate-400">
                <span>Didn&rsquo;t get the code?</span>
                {cooldown > 0 ? (
                  <span className="font-semibold text-slate-400 tabular-nums">
                    Resend in {cooldown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    className="font-bold text-brand transition hover:underline"
                  >
                    Resend code
                  </button>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand to-brand-dark text-sm font-bold text-white shadow-lg shadow-brand/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-brand/40 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Verifying…
                  </>
                ) : (
                  <>
                    <ShieldCheck size={16} strokeWidth={2.1} />
                    Verify code
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationCode;
